import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraOverlayDialog } from '@mono/ui-components/captains';
import { SpecraButton, SpecraSelect, SpecraText, SpecraTextarea } from '@mono/ui-components/ensigns';
import { SpecraCombobox, SpecraFormField, SpecraInlineBanner, SpecraSegmentedControl } from '@mono/ui-components/lieutenants';
import { Testids } from '@mono/ui-selectors';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { deliveryClient, projectDataClient } from '../../../../lib/connect/clients';
import { usePricingWrite } from './usePricingRateBook';
const T = Testids.pricingWorkspace;
export function LinkDecisionDialog({ scope, link, companyId, vehicleId, vehicleName, onClose }) {
    const [decision, setDecision] = useState(link.decision || 'price_from_source_vehicle');
    const [reason, setReason] = useState('');
    const [sourceVehicle, setSourceVehicle] = useState(link.sourceVehicleId);
    const [entryId, setEntryId] = useState('');
    const vehicles = useQuery({
        queryKey: [
            'pricing-vehicles',
            scope.principal,
            companyId
        ],
        staleTime: 0,
        queryFn: ()=>deliveryClient.listContractVehicles({
                companyId
            })
    });
    const entries = useQuery({
        queryKey: [
            'pricing-remap-entries',
            scope.principal,
            companyId,
            vehicleId
        ],
        enabled: !!vehicleId,
        staleTime: 0,
        queryFn: ()=>deliveryClient.listLaborCategoryEntries({
                companyId,
                contractVehicleId: vehicleId
            })
    });
    const entry = entries.data?.entries.find((e)=>e.id === entryId && e.contractVehicleId === vehicleId);
    const defaultVehicle = vehicles.data?.vehicles.find((v)=>v.name === link.entryVehicleName)?.id || '';
    const selectedVehicle = sourceVehicle || defaultVehicle;
    const write = usePricingWrite(()=>projectDataClient.decideLcatRegistryLink({
            projectId: scope.projectId,
            lcatLogicalKey: link.lcatLogicalKey,
            decision,
            sourceVehicleId: selectedVehicle,
            entryId,
            entryVersion: entry?.version,
            reason
        }));
    return /*#__PURE__*/ _jsx(SpecraOverlayDialog, {
        open: true,
        onOpenChange: (v)=>{
            if (!v && !write.isPending) onClose();
        },
        title: "Decide how to price this labor category",
        testId: T.decisionDialog,
        footer: /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsx(SpecraButton, {
                    variant: "secondary",
                    onClick: onClose,
                    children: "Cancel"
                }),
                /*#__PURE__*/ _jsx(SpecraButton, {
                    "data-testid": T.save,
                    disabled: !reason.trim() || write.isPending || decision === 'remap' && !entry || decision === 'price_from_source_vehicle' && !selectedVehicle,
                    onClick: async ()=>{
                        try {
                            await write.mutateAsync();
                            onClose();
                        } catch  {
                        /* inline */ }
                    },
                    children: "Save decision"
                })
            ]
        }),
        children: /*#__PURE__*/ _jsxs("div", {
            className: "space-y-4",
            children: [
                /*#__PURE__*/ _jsx(SpecraSegmentedControl, {
                    label: "Decision",
                    testId: T.decisionOption,
                    value: decision,
                    onValueChange: setDecision,
                    options: [
                        {
                            value: 'price_from_source_vehicle',
                            label: `Price from ${link.entryVehicleName || 'source vehicle'}`
                        },
                        {
                            value: 'remap',
                            label: `Map to a ${vehicleName || 'proposal vehicle'} category`,
                            disabled: !vehicleId
                        },
                        {
                            value: 'as_written_only',
                            label: 'Price as written only'
                        }
                    ]
                }),
                /*#__PURE__*/ _jsx(SpecraText, {
                    children: decision === 'remap' ? `Pick the ${vehicleName} category this RFP title really means.` : decision === 'as_written_only' ? 'No catalog rates; enter the rate by hand.' : `We will use ${link.entryVehicleName} rates for this row on purpose. Nothing is added to the ${vehicleName || 'proposal vehicle'} catalog.`
                }),
                decision === 'remap' && /*#__PURE__*/ _jsx(SpecraCombobox, {
                    label: "Map to",
                    loading: entries.isLoading,
                    options: (entries.data?.entries ?? []).filter((e)=>e.contractVehicleId === vehicleId).map((e)=>({
                            value: e.id,
                            label: e.title
                        })),
                    value: entryId,
                    onValueChange: (v)=>setEntryId(v || '')
                }),
                decision === 'price_from_source_vehicle' && /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "Source vehicle",
                    children: /*#__PURE__*/ _jsxs(SpecraSelect, {
                        "aria-label": "Source vehicle",
                        value: selectedVehicle,
                        onChange: (e)=>setSourceVehicle(e.target.value),
                        children: [
                            /*#__PURE__*/ _jsx("option", {
                                value: "",
                                children: "Select vehicle"
                            }),
                            vehicles.data?.vehicles.map((v)=>/*#__PURE__*/ _jsx("option", {
                                    value: v.id,
                                    children: v.name
                                }, v.id))
                        ]
                    })
                }),
                /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "Reason",
                    htmlFor: "mapping-reason",
                    required: true,
                    children: /*#__PURE__*/ _jsx(SpecraTextarea, {
                        id: "mapping-reason",
                        value: reason,
                        onChange: (e)=>setReason(e.target.value)
                    })
                }),
                /*#__PURE__*/ _jsxs(SpecraInlineBanner, {
                    tone: "info",
                    children: [
                        "This will not add the title to the ",
                        vehicleName || 'proposal vehicle',
                        " catalog."
                    ]
                }),
                (write.error || entries.error || vehicles.error) && /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                    tone: "danger",
                    children: (write.error || entries.error || vehicles.error)?.message
                })
            ]
        })
    });
}
